import {
  isEmptyCompositionBucket,
  isOtherCompositionBucket,
} from '@revelio/core/composition-buckets';
import type { IcicleNode } from '@revelio/replots';

import { promoteLoneTopLevelNode } from '../../../Chart/Icicle/icicle-data';
import {
  MAX_PARENT_GROUPS,
  taxonomyCoverageMaxGroups,
} from '../../../Chart/utils/buildNestedTreemapData';
import type { TreemapTaxonomyCoverage } from '../../../PageConfigProvider/types/item';
import { DataType, type TableRowsDataSet } from '../../types';
import {
  type CompositionV3AnyGraphqlResponse,
  isCompositionV3FullMetrics,
} from '../composition-v3/composition-v3-graphql-response';
import { pointAtSnapshot } from '../composition-v3/convert-composition-data-v3';
import {
  icicleTiersBelowRoot,
  type TaxonomyIcicleDrill,
  type TaxonomyIcicleEntity,
} from './plan-taxonomy-icicle-requests';
import type { IcicleTaxonomySegment } from './taxonomies/types';
import type {
  TaxonomyChainStep,
  TaxonomyIcicleIndex,
} from './taxonomy-icicle-index';

export type TaxonomyIcicleResponse = {
  key: string;
  depth: number;
  scope: TaxonomyIcicleDrill | null;
  payload: CompositionV3AnyGraphqlResponse;
};

type TaxonomyIcicleTier = {
  counts: ReadonlyMap<string, number>;
  headcounts: ReadonlyMap<string, number>;
  complete: boolean;
  /** The entity's share of itself: all of it, or nothing without headcount at the snapshot. */
  total: number;
  headcount: number;
};

export type TaxonomyIcicleSource = {
  key: string;
  depth: number;
  scope: TaxonomyIcicleDrill | null;
  tiers: ReadonlyMap<string, TaxonomyIcicleTier>;
};

type TaxonomyIcicleNodeMeta = {
  entityKey: string;
  total: number;
  count: number;
  isEntity: boolean;
  path: readonly TaxonomyIcicleDrill[];
  drillPath: readonly TaxonomyIcicleDrill[] | null;
};

export type TaxonomyIcicleTree = {
  nodes: IcicleNode[];
  meta: Map<string, TaxonomyIcicleNodeMeta>;
  totalWeight: number;
  entityTotals: Map<string, number>;
};

export type TaxonomyIcicleData = {
  icicle: TaxonomyIcicleTree;
  exportData: TableRowsDataSet | null;
};

type Aggregate = {
  id: string;
  name: string;
  levelId: string;
  depth: number;
  total: number;
  count: number;
  exact: boolean;
  children: Map<string, Aggregate>;
};

type EntityTiers = {
  tierOne: Map<string, Aggregate>;
  entityTotal: number;
  entityCount: number;
};

const DEFAULT_MAX_CELLS = 50;

const byTotal = (a: Aggregate, b: Aggregate) => b.total - a.total;

const sumTotals = (aggregates: Iterable<Aggregate>) => {
  let sum = 0;
  for (const aggregate of aggregates) sum += aggregate.total;
  return sum;
};

const sumCounts = (aggregates: Iterable<Aggregate>) => {
  let sum = 0;
  for (const aggregate of aggregates) sum += aggregate.count;
  return sum;
};

const hasRemainder = (remainder: number, total: number) =>
  remainder > Math.max(Math.abs(total), 1) * 1e-9;

const deepestSource = (
  sources: readonly TaxonomyIcicleSource[]
): TaxonomyIcicleSource | undefined =>
  sources.reduce<TaxonomyIcicleSource | undefined>(
    (deepest, source) =>
      deepest === undefined ||
      source.depth > deepest.depth ||
      (source.depth === deepest.depth &&
        deepest.scope === null &&
        source.scope !== null)
        ? source
        : deepest,
    undefined
  );

export function toTaxonomyIcicleSources(
  responses: readonly TaxonomyIcicleResponse[],
  entityKeys: readonly string[],
  segment: IcicleTaxonomySegment
): TaxonomyIcicleSource[] {
  return responses.map(({ key, depth, scope, payload }) => {
    const tiers = new Map<string, TaxonomyIcicleTier>();
    (payload.entities ?? []).forEach((entity, position) => {
      const entityKey = entityKeys[position];
      if (
        entityKey === undefined ||
        !isCompositionV3FullMetrics(entity.metrics)
      ) {
        return;
      }
      // Activity buckets carry their whole timeseries even for a snapshot
      // request: read the point of the entity's own snapshot, not the last one.
      const headcount = entity.metrics.headcount;
      const snapshotId = headcount[headcount.length - 1]?.id;
      const counts = new Map<string, number>();
      const headcounts = new Map<string, number>();
      let remainder = 0;
      for (const bucket of entity.metrics[segment] ?? []) {
        const point = pointAtSnapshot(bucket.headcount, snapshotId);
        const count = point?.share ?? 0;
        if (isOtherCompositionBucket(bucket.id)) {
          remainder += count;
        } else if (!isEmptyCompositionBucket(bucket.id)) {
          const id = String(bucket.id);
          counts.set(id, (counts.get(id) ?? 0) + count);
          headcounts.set(id, (headcounts.get(id) ?? 0) + (point?.count ?? 0));
        }
      }
      const entityHeadcount = headcount[headcount.length - 1]?.val ?? 0;
      tiers.set(entityKey, {
        counts,
        headcounts,
        complete: remainder <= 0,
        total: entityHeadcount > 0 ? 1 : 0,
        headcount: entityHeadcount,
      });
    });
    return { key, depth, scope, tiers };
  });
}

type EntityTierContext = {
  index: TaxonomyIcicleIndex;
  entity: TaxonomyIcicleEntity;
  sources: readonly TaxonomyIcicleSource[];
  rootDepth: number;
  lastDepth: number;
  drill: { levelId: string; id: string } | null;
  drillDepth: number;
  tiersBelowRoot: number;
};

const newAggregate = (
  step: TaxonomyChainStep,
  levelId: string,
  depth: number,
  total: number,
  count: number,
  exact: boolean
): Aggregate => ({
  id: step.id,
  name: step.name,
  levelId,
  depth,
  total,
  count,
  exact,
  children: new Map(),
});

/**
 * Rolled-up taxonomies: every leaf of the deepest complete response is added
 * to each of its ancestors, so the tiers above the leaf are exact sums. A
 * deeper capped response hangs under those exact totals as partial cells.
 */
const collectRolledUpTiers = ({
  index,
  entity,
  sources,
  rootDepth,
  lastDepth,
  drill,
  drillDepth,
  tiersBelowRoot,
}: EntityTierContext): EntityTiers => {
  const windowLeaf = Math.min(rootDepth + tiersBelowRoot, lastDepth);
  const tierOf = (source: TaxonomyIcicleSource) => source.tiers.get(entity.key);
  const underDrill = (chain: readonly TaxonomyChainStep[]) =>
    drill === null || drillDepth === -1 || chain[drillDepth]?.id === drill.id;

  const coversTaxonomy = (source: TaxonomyIcicleSource) => {
    const tier = tierOf(source);
    if (tier?.complete !== true) return false;
    const known = index.chainsByDepth[source.depth];
    for (const [id, count] of tier.counts) {
      if (count > 0 && known?.has(id) !== true) return false;
    }
    return true;
  };

  const candidates = sources.filter(
    (source) =>
      source.depth >= Math.min(rootDepth + 1, lastDepth) &&
      tierOf(source) !== undefined
  );
  const completeBase = deepestSource(candidates.filter(coversTaxonomy));
  const base =
    completeBase ??
    deepestSource(candidates.filter((source) => source.depth <= windowLeaf));
  const baseDepth =
    base === undefined ? rootDepth : Math.min(base.depth, windowLeaf);
  const partial =
    completeBase === undefined
      ? undefined
      : deepestSource(
          candidates.filter(
            (source) => source.depth > baseDepth && source.depth <= windowLeaf
          )
        );

  const tierOne = new Map<string, Aggregate>();
  const accumulate = (
    level: Map<string, Aggregate>,
    chain: readonly TaxonomyChainStep[],
    fromDepth: number,
    toDepth: number,
    count: number,
    headcount: number,
    exact: boolean
  ) => {
    let current = level;
    for (let depth = fromDepth; depth <= toDepth; depth++) {
      const step = chain[depth];
      const levelId = index.levelIds[depth];
      if (step === undefined || levelId === undefined) return;
      let aggregate = current.get(step.id);
      if (aggregate === undefined) {
        aggregate = newAggregate(step, levelId, depth, 0, 0, exact);
        current.set(step.id, aggregate);
      }
      aggregate.total += count;
      aggregate.count += headcount;
      current = aggregate.children;
    }
  };
  const anchorAt = (chain: readonly TaxonomyChainStep[], depth: number) => {
    let current: Aggregate | undefined;
    let level = tierOne;
    for (let step = rootDepth + 1; step <= depth; step++) {
      const id = chain[step]?.id;
      current = id === undefined ? undefined : level.get(id);
      if (current === undefined) return undefined;
      level = current.children;
    }
    return current;
  };

  let entityTotal = 0;
  let entityCount = 0;
  if (base !== undefined) {
    const tier = tierOf(base);
    for (const [id, count] of tier?.counts ?? []) {
      if (count <= 0) continue;
      const chain = index.chainsByDepth[base.depth]?.get(id);
      if (chain === undefined || !underDrill(chain)) continue;
      const headcount = tier?.headcounts.get(id) ?? 0;
      entityTotal += count;
      entityCount += headcount;
      accumulate(
        tierOne,
        chain,
        rootDepth + 1,
        baseDepth,
        count,
        headcount,
        completeBase !== undefined
      );
    }
  }
  if (partial !== undefined) {
    const tier = tierOf(partial);
    for (const [id, count] of tier?.counts ?? []) {
      if (count <= 0) continue;
      const chain = index.chainsByDepth[partial.depth]?.get(id);
      if (chain === undefined || !underDrill(chain)) continue;
      const anchor = anchorAt(chain, baseDepth);
      if (anchor === undefined) continue;
      accumulate(
        anchor.children,
        chain,
        baseDepth + 1,
        partial.depth,
        count,
        tier?.headcounts.get(id) ?? 0,
        false
      );
    }
  }
  return { tierOne, entityTotal, entityCount };
};

/**
 * Overlapping taxonomies: each drawn tier comes from its own response and
 * every node keeps the count it was returned with. A node only hangs under a
 * parent the tier above returned, and the root's own count is the entity's
 * headcount, or the drilled node's count in the chip-level tier it came from.
 */
const collectOverlappingTiers = ({
  index,
  entity,
  sources,
  rootDepth,
  lastDepth,
  drill,
  drillDepth,
  tiersBelowRoot,
}: EntityTierContext): EntityTiers => {
  const windowLeaf = Math.min(rootDepth + tiersBelowRoot, lastDepth);
  const tierOf = (source: TaxonomyIcicleSource) => source.tiers.get(entity.key);
  const underDrill = (chain: readonly TaxonomyChainStep[]) =>
    drill === null || drillDepth === -1 || chain[drillDepth]?.id === drill.id;
  const withTier = sources.filter((source) => tierOf(source) !== undefined);
  const sourceAt = (depth: number) => {
    const atDepth = withTier.filter((source) => source.depth === depth);
    return (
      atDepth.find((source) => tierOf(source)?.complete === true) ??
      atDepth.find((source) => source.scope !== null) ??
      atDepth[0]
    );
  };

  const tierOne = new Map<string, Aggregate>();
  for (let depth = rootDepth + 1; depth <= windowLeaf; depth++) {
    const source = sourceAt(depth);
    if (source === undefined) continue;
    const tier = tierOf(source);
    for (const [id, count] of tier?.counts ?? []) {
      if (count <= 0) continue;
      const chain = index.chainsByDepth[depth]?.get(id);
      const step = chain?.[depth];
      const levelId = index.levelIds[depth];
      if (
        chain === undefined ||
        step === undefined ||
        levelId === undefined ||
        !underDrill(chain)
      ) {
        continue;
      }
      let level: Map<string, Aggregate> | undefined = tierOne;
      for (let above = rootDepth + 1; above < depth && level; above++) {
        level = level.get(chain[above]?.id ?? '')?.children;
      }
      if (level === undefined) continue;
      level.set(
        step.id,
        newAggregate(
          step,
          levelId,
          depth,
          count,
          tier?.headcounts.get(id) ?? 0,
          true
        )
      );
    }
  }

  const root = (() => {
    if (drill !== null && drillDepth > entity.rootDepth) {
      const drilledTier = sourceAt(drillDepth);
      const tier = drilledTier === undefined ? undefined : tierOf(drilledTier);
      return {
        total: tier?.counts.get(drill.id),
        count: tier?.headcounts.get(drill.id),
      };
    }
    const [first] = withTier;
    const tier = first === undefined ? undefined : tierOf(first);
    return { total: tier?.total, count: tier?.headcount };
  })();
  const rootTotal =
    root.total !== undefined && root.total > 0 ? root.total : undefined;
  return {
    tierOne,
    entityTotal: rootTotal ?? sumTotals(tierOne.values()),
    entityCount:
      rootTotal !== undefined && root.count !== undefined
        ? root.count
        : sumCounts(tierOne.values()),
  };
};

export function buildTaxonomyIcicleTree({
  index,
  entities,
  sources,
  drill,
  overlapping,
  tiersBelowRoot: configuredTiersBelowRoot,
  maxGroups,
  maxCells,
  omitOther,
}: {
  index: TaxonomyIcicleIndex;
  entities: readonly TaxonomyIcicleEntity[];
  sources: readonly TaxonomyIcicleSource[];
  drill: { levelId: string; id: string } | null;
  overlapping: boolean;
  tiersBelowRoot?: number;
  maxGroups: number;
  maxCells: number;
  omitOther: boolean;
}): TaxonomyIcicleTree {
  const lastDepth = index.levelIds.length - 1;
  const tiersBelowRoot = icicleTiersBelowRoot(
    overlapping,
    configuredTiersBelowRoot
  );
  const drillDepth =
    drill === null ? -1 : index.levelIds.indexOf(drill.levelId);
  const showEntities = entities.length > 1;

  const nodes: IcicleNode[] = [];
  const meta = new Map<string, TaxonomyIcicleNodeMeta>();
  const entityTotals = new Map<string, number>();

  /**
   * How wide the kept children of a parent are drawn, and what is left for a
   * grey Other (its width, and the count its tooltip reports). Rolled-up tiers
   * keep their true counts, so a capped child shrinks the parent unless Other
   * absorbs it; overlapping tiers are shares of the parent's width,
   * renormalised over the kept children when Other is omitted.
   */
  const weighChildren = (
    parent: { weight: number; total: number; count: number; exact: boolean },
    all: readonly Aggregate[],
    kept: readonly Aggregate[]
  ): {
    weights: Map<Aggregate, number>;
    otherWeight: number;
    otherTotal: number;
    otherCount: number;
  } => {
    const capped = sumTotals(all) - sumTotals(kept);
    const cappedCount = sumCounts(all) - sumCounts(kept);
    if (!overlapping) {
      const exact = parent.exact && all.length > 0;
      const dataRemainder = exact ? parent.total - sumTotals(all) : 0;
      const countRemainder = exact ? parent.count - sumCounts(all) : 0;
      const otherWeight = (omitOther ? 0 : capped) + dataRemainder;
      return {
        weights: new Map(kept.map((child) => [child, child.total])),
        otherWeight,
        otherTotal: otherWeight,
        otherCount: (omitOther ? 0 : cappedCount) + countRemainder,
      };
    }
    const denominator = sumTotals(omitOther ? kept : all);
    const share = (total: number) =>
      denominator > 0 ? (parent.weight * total) / denominator : 0;
    return {
      weights: new Map(kept.map((child) => [child, share(child.total)])),
      otherWeight: omitOther ? 0 : share(capped),
      otherTotal: capped,
      otherCount: cappedCount,
    };
  };

  for (const entity of entities) {
    const rootDepth = Math.max(entity.rootDepth, drillDepth);
    const prefix = showEntities ? `${entity.key}:` : '';
    const entityNodeId = showEntities ? `entity:${entity.key}` : null;
    const context: EntityTierContext = {
      index,
      entity,
      sources,
      rootDepth,
      lastDepth,
      drill,
      drillDepth,
      tiersBelowRoot,
    };
    const { tierOne, entityTotal, entityCount } = overlapping
      ? collectOverlappingTiers(context)
      : collectRolledUpTiers(context);

    const maxDrillDepth = overlapping
      ? entity.rootDepth + tiersBelowRoot
      : lastDepth;
    const canDrill = (depth: number) =>
      depth < lastDepth && depth <= maxDrillDepth;
    // A bottom-level cell opens its parent, as a treemap leaf opens its group.
    const drillPathOf = (
      depth: number,
      path: readonly TaxonomyIcicleDrill[],
      parentPath: readonly TaxonomyIcicleDrill[]
    ) => {
      if (canDrill(depth)) return path;
      if (depth === lastDepth && parentPath.length > 0 && canDrill(depth - 1)) {
        return parentPath;
      }
      return null;
    };
    const entityNodes: IcicleNode[] = [];

    // Drilled one level above the leaves, the drilled node stays drawn as the
    // column its leaves hang from.
    const drilledNode = (() => {
      if (
        drill === null ||
        drillDepth !== lastDepth - 1 ||
        drillDepth <= entity.rootDepth
      ) {
        return null;
      }
      const step = index.chainsByDepth[drillDepth]?.get(drill.id)?.[drillDepth];
      if (step === undefined) return null;
      return {
        id: `${prefix}${drill.levelId}:${step.id}`,
        step: { levelId: drill.levelId, id: step.id, name: step.name },
      };
    })();
    if (drilledNode !== null) {
      entityNodes.push({
        id: drilledNode.id,
        parentId: entityNodeId,
        name: drilledNode.step.name,
        weight: entityTotal,
      });
      meta.set(drilledNode.id, {
        entityKey: entity.key,
        total: entityTotal,
        count: entityCount,
        isEntity: false,
        path: [drilledNode.step],
        drillPath: null,
      });
    }
    const rootId = drilledNode?.id ?? entityNodeId;

    const emit = (
      aggregate: Aggregate,
      parentId: string | null,
      parentPath: readonly TaxonomyIcicleDrill[],
      weight: number
    ) => {
      const id = `${prefix}${aggregate.levelId}:${aggregate.id}`;
      const path = [
        ...parentPath,
        { levelId: aggregate.levelId, id: aggregate.id, name: aggregate.name },
      ];
      entityNodes.push({ id, parentId, name: aggregate.name, weight });
      meta.set(id, {
        entityKey: entity.key,
        total: aggregate.total,
        count: aggregate.count,
        isEntity: false,
        path,
        drillPath: drillPathOf(aggregate.depth, path, parentPath),
      });
      return { id, path, weight, depth: aggregate.depth };
    };

    const emitOther = (
      id: string,
      parent: {
        id: string | null;
        path: readonly TaxonomyIcicleDrill[];
        depth: number;
      },
      weight: number,
      total: number,
      count: number
    ) => {
      entityNodes.push({
        id,
        parentId: parent.id,
        name: 'Other',
        weight,
        isOther: true,
      });
      meta.set(id, {
        entityKey: entity.key,
        total,
        count,
        isEntity: false,
        path: [
          ...parent.path,
          {
            levelId: index.levelIds[parent.depth + 1] ?? '',
            id: 'other',
            name: 'Other',
          },
        ],
        drillPath: null,
      });
    };

    const emitDescendants = (
      aggregate: Aggregate,
      ref: {
        id: string;
        path: readonly TaxonomyIcicleDrill[];
        weight: number;
        depth: number;
      }
    ) => {
      const children = [...aggregate.children.values()].sort(byTotal);
      const { weights, otherWeight, otherTotal, otherCount } = weighChildren(
        {
          weight: ref.weight,
          total: aggregate.total,
          count: aggregate.count,
          exact: aggregate.exact,
        },
        children,
        children
      );
      for (const child of children) {
        emitDescendants(
          child,
          emit(child, ref.id, ref.path, weights.get(child) ?? 0)
        );
      }
      if (hasRemainder(otherWeight, ref.weight)) {
        emitOther(`${ref.id}:other`, ref, otherWeight, otherTotal, otherCount);
      }
    };

    const ranked = [...tierOne.values()].sort(byTotal);
    const topGroups = ranked.slice(0, maxGroups);
    const keptCells = new Set(
      topGroups
        .flatMap((group) => [...group.children.values()])
        .sort(byTotal)
        .slice(0, maxCells)
    );
    const cellsOf = (group: Aggregate) =>
      [...group.children.values()]
        .filter((cell) => keptCells.has(cell))
        .sort(byTotal);
    const groups = topGroups.filter((group) => {
      const all = [...group.children.values()];
      if (all.length === 0 || cellsOf(group).length > 0) return true;
      const { otherWeight } = weighChildren(
        {
          weight: group.total,
          total: group.total,
          count: group.count,
          exact: group.exact,
        },
        all,
        []
      );
      return hasRemainder(otherWeight, group.total);
    });

    const entityLevel = weighChildren(
      {
        weight: entityTotal,
        total: entityTotal,
        count: entityCount,
        exact: false,
      },
      ranked,
      groups
    );
    for (const group of groups) {
      const groupRef = emit(
        group,
        rootId,
        [],
        entityLevel.weights.get(group) ?? 0
      );
      const all = [...group.children.values()];
      const cells = cellsOf(group);
      const { weights, otherWeight, otherTotal, otherCount } = weighChildren(
        {
          weight: groupRef.weight,
          total: group.total,
          count: group.count,
          exact: group.exact,
        },
        all,
        cells
      );
      for (const cell of cells) {
        emitDescendants(
          cell,
          emit(cell, groupRef.id, groupRef.path, weights.get(cell) ?? 0)
        );
      }
      if (all.length > 0 && hasRemainder(otherWeight, groupRef.weight)) {
        emitOther(
          `${groupRef.id}:other`,
          groupRef,
          otherWeight,
          otherTotal,
          otherCount
        );
      }
    }
    if (hasRemainder(entityLevel.otherWeight, entityTotal)) {
      emitOther(
        `${prefix}other`,
        { id: rootId, path: [], depth: rootDepth },
        entityLevel.otherWeight,
        entityLevel.otherTotal,
        entityLevel.otherCount
      );
    }

    if (entityTotal <= 0) continue;
    entityTotals.set(entity.key, entityTotal);
    if (entityNodeId !== null || entityNodes.length === 0) {
      const id = entityNodeId ?? `entity:${entity.key}`;
      nodes.push({
        id,
        parentId: null,
        name: entity.name,
        weight: entityTotal,
      });
      meta.set(id, {
        entityKey: entity.key,
        total: entityTotal,
        count: entityCount,
        isEntity: true,
        path: [],
        drillPath: null,
      });
    }
    nodes.push(...entityNodes);
  }

  const totalWeight = [...entityTotals.values()].reduce(
    (sum, weight) => sum + weight,
    0
  );

  return {
    nodes: drill === null ? promoteLoneTopLevelNode(nodes) : nodes,
    meta,
    totalWeight,
    entityTotals,
  };
}

type TaxonomyIcicleExportRow = Record<string, string | number>;

const uniqueColumn = (label: string, taken: Set<string>): string => {
  let column = label;
  for (let suffix = 2; taken.has(column); suffix++) {
    column = `${label} (${suffix})`;
  }
  taken.add(column);
  return column;
};

function buildTaxonomyIcicleExportRows({
  icicle,
  index,
  entities,
  drill,
  entityLabel,
  levelLabels,
}: {
  icicle: TaxonomyIcicleTree;
  index: TaxonomyIcicleIndex;
  entities: readonly TaxonomyIcicleEntity[];
  drill: TaxonomyIcicleDrill | null;
  entityLabel: string;
  levelLabels: readonly string[];
}): TaxonomyIcicleExportRow[] {
  const depthOf = new Map(
    index.levelIds.map((levelId, depth) => [levelId, depth])
  );
  const drillDepth = drill === null ? -1 : (depthOf.get(drill.levelId) ?? -1);
  const drillChain =
    drill === null || drillDepth === -1
      ? []
      : (index.chainsByDepth[drillDepth]?.get(drill.id) ?? []);
  const entityByKey = new Map(entities.map((entity) => [entity.key, entity]));

  const cells = icicle.nodes.flatMap((node) => {
    const meta = icicle.meta.get(node.id);
    const entity =
      meta === undefined ? undefined : entityByKey.get(meta.entityKey);
    if (meta === undefined || entity === undefined) return [];
    const namesByDepth = new Map<number, string>();
    drillChain.forEach((step, depth) => {
      if (depth > entity.rootDepth) namesByDepth.set(depth, step.name);
    });
    for (const step of meta.path) {
      const depth = depthOf.get(step.levelId);
      if (depth !== undefined) namesByDepth.set(depth, step.name);
    }
    return [{ entity, meta, namesByDepth }];
  });
  if (cells.length === 0) return [];

  const firstDepth = Math.min(
    ...cells.map(({ entity }) => entity.rootDepth + 1)
  );
  const lastDepth = Math.max(
    firstDepth - 1,
    ...cells.flatMap(({ namesByDepth }) => [...namesByDepth.keys()])
  );
  const taken = new Set<string>();
  const entityColumn = uniqueColumn(entityLabel, taken);
  const levelColumns: [number, string][] = [];
  for (let depth = firstDepth; depth <= lastDepth; depth++) {
    levelColumns.push([
      depth,
      uniqueColumn(levelLabels[depth] ?? `Level ${depth + 1}`, taken),
    ]);
  }
  const countColumn = uniqueColumn('Count', taken);
  const shareColumn = uniqueColumn('Share', taken);

  return cells.map(({ entity, meta, namesByDepth }) => {
    const row: TaxonomyIcicleExportRow = { [entityColumn]: entity.name };
    for (const [depth, column] of levelColumns) {
      row[column] = namesByDepth.get(depth) ?? '';
    }
    row[countColumn] = meta.count;
    row[shareColumn] = meta.total;
    return row;
  });
}

export function convertTaxonomyIcicleData({
  segment,
  overlapping,
  tiersBelowRoot,
  index,
  entities,
  responses,
  drill,
  taxonomyCoverage,
  entityLabel,
  levelLabels,
}: {
  segment: IcicleTaxonomySegment;
  overlapping: boolean;
  tiersBelowRoot?: number;
  index: TaxonomyIcicleIndex;
  entities: readonly TaxonomyIcicleEntity[];
  responses: readonly TaxonomyIcicleResponse[];
  drill: TaxonomyIcicleDrill | null;
  taxonomyCoverage: TreemapTaxonomyCoverage | undefined;
  entityLabel: string;
  levelLabels: readonly string[];
}): TaxonomyIcicleData {
  const icicle = buildTaxonomyIcicleTree({
    index,
    entities,
    sources: toTaxonomyIcicleSources(
      responses,
      entities.map((entity) => entity.key),
      segment
    ),
    drill,
    overlapping,
    tiersBelowRoot,
    maxGroups: taxonomyCoverageMaxGroups(taxonomyCoverage) ?? MAX_PARENT_GROUPS,
    maxCells: taxonomyCoverage?.topN?.cells ?? DEFAULT_MAX_CELLS,
    omitOther: taxonomyCoverage?.omitOther === true,
  });
  const rows = buildTaxonomyIcicleExportRows({
    icicle,
    index,
    entities,
    drill,
    entityLabel,
    levelLabels,
  });
  return {
    icicle,
    exportData:
      rows.length === 0 ? null : { type: DataType.TABLE_ROWS, data: rows },
  };
}
